import { Link } from "react-router-dom";
import { Reveal, Chapter } from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "../data/products";
import { useLang, useT } from "../i18n";

const Products = () => {
  const { lang } = useLang();
  const t = useT();
  return (
    <section id="products" data-testid="products-section" className="bg-[#0B0E14] py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter number="01" label={t.productsSection.label} testid="products-chapter" />
        </Reveal>
        <div className="mt-8 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <Reveal delay={0.1}>
            <h2 data-testid="products-title" className="max-w-xl font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
              {t.productsSection.title}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="font-display text-lg italic text-gold md:text-xl">{t.productsSection.sub}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug} delay={0.06 * i}>
              <article
                data-testid={`product-card-${p.slug}`}
                className="group flex h-full flex-col border border-slate-800 bg-[#0B132B] transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/40"
              >
                <Link to={`/products/${p.slug}`} className="relative block aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name.en}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/70 via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 bg-[#0B0E14]/80 px-3 py-1.5 font-mono text-[10px] tracking-[0.3em] text-gold backdrop-blur-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-white">
                    {p.name[lang]}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc[lang]}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.subcats[lang].map((s) => (
                      <span key={s} className="border border-slate-700 px-2 py-1 font-mono text-[9px] tracking-[0.12em] text-slate-400">
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center gap-3 border-t border-slate-800 pt-5">
                    <Link
                      data-testid={`product-view-${p.slug}`}
                      to={`/products/${p.slug}`}
                      className="group/btn flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] text-gold transition-colors duration-300 hover:text-gold-light"
                    >
                      {t.productsSection.view.toUpperCase()}
                      <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </Link>
                    <span className="h-3 w-px bg-slate-700" />
                    <Link
                      data-testid={`product-quote-${p.slug}`}
                      to={`/?product=${p.slug}#quote`}
                      className="font-mono text-[11px] tracking-[0.2em] text-slate-400 transition-colors duration-300 hover:text-gold"
                    >
                      {t.productsSection.quote.toUpperCase()}
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
